<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { ChevronDown, Check, AlertCircle } from '@lucide/vue'

interface IProps {
  label?: string
  placeholder?: string
  error?: string
  disabled?: boolean
  options: Array<{
    caption: string
    value: string | number | boolean
  }>
}
const props = defineProps<IProps>()

const model = defineModel<string | number | boolean>()

const selectOpen = ref(false)
const rootEl = ref<HTMLElement | null>(null)
const dropdownEl = ref<HTMLElement | null>(null)

const dropdownStyle = ref({ top: '0px', left: '0px', width: '0px' })

const isDismissed = ref(false)

watch(
  () => props.error,
  (newError) => {
    if (newError) {
      isDismissed.value = false
    }
  },
)

const hasError = computed(() => !!props.error && !isDismissed.value)

const selected = computed(() => {
  const found = props.options.find((o) => o.value === model.value)
  return found?.caption ?? props.placeholder ?? ''
})

function updatePosition() {
  if (!rootEl.value) return
  const rect = rootEl.value.getBoundingClientRect()
  dropdownStyle.value = {
    top: `${rect.bottom + window.scrollY + 4}px`,
    left: `${rect.left + window.scrollX}px`,
    width: `${rect.width}px`,
  }
}

function toggleOpen() {
  selectOpen.value = !selectOpen.value
  if (selectOpen.value) nextTick(updatePosition)
}

function selectOption(value: string | number | boolean | undefined) {
  model.value = value
  selectOpen.value = false
  if (props.error) {
    isDismissed.value = true
  }
}

function handleClickOutside(event: MouseEvent) {
  const target = event.target as Node
  const insideRoot = rootEl.value?.contains(target)
  const insideDropdown = dropdownEl.value?.contains(target)
  if (!insideRoot && !insideDropdown) {
    selectOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside)
  window.addEventListener('scroll', updatePosition, true)
  window.addEventListener('resize', updatePosition)
})

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside)
  window.removeEventListener('scroll', updatePosition, true)
  window.removeEventListener('resize', updatePosition)
})
</script>

<template>
  <div ref="rootEl" class="space-y-1 relative">
    <label
      v-if="label"
      class="text-sm mb-1.5 inline-block font-bold text-foreground dark:text-white"
      >{{ label }}</label
    >
    <button
      type="button"
      @click="toggleOpen"
      :disabled="disabled"
      class="w-full h-9 px-3 rounded-lg border border-border bg-background text-sm text-foreground flex items-center justify-between hover:bg-muted/50 transition-all focus:outline-none focus:ring-1 focus:ring-primary/50 cursor-pointer dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
      :class="[
        hasError
          ? 'border-destructive/50 focus:ring-destructive/20'
          : 'border-input focus:ring-primary/50',
        disabled ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800' : '',
      ]"
    >
      <span>{{ selected }}</span>
      <ChevronDown
        :size="14"
        class="text-muted-foreground transition-transform duration-150"
        :class="{ 'rotate-180': selectOpen }"
      />
    </button>

    <Teleport to="body">
      <div
        v-if="selectOpen"
        ref="dropdownEl"
        class="fixed z-[99999] bg-card border border-border rounded-xl shadow-xl py-1 overflow-y-auto"
        :style="{ ...dropdownStyle, maxHeight: '260px' }"
      >
        <button
          type="button"
          @click="selectOption(undefined)"
          class="w-full flex justify-end text-left px-3 py-2 text-sm hover:bg-muted transition-all flex items-center justify-between cursor-pointer"
          :class="model === undefined ? 'text-primary font-semibold' : 'text-muted-foreground/60'"
        >
          {{ placeholder || '\u00A0' }}
          <Check v-if="model === undefined" :size="13" class="text-primary" />
        </button>
        <button
          v-for="option in options"
          :key="String(option.value)"
          type="button"
          @click="selectOption(option.value)"
          class="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-all flex items-center justify-between cursor-pointer"
          :class="option.value === model ? 'text-primary font-semibold' : 'text-foreground'"
        >
          {{ option.caption }}
          <Check v-if="option.value === model" :size="13" class="text-primary" />
        </button>
      </div>
    </Teleport>

    <p v-if="hasError" class="text-xs text-destructive flex items-center gap-1.5">
      <AlertCircle :size="11" /> {{ props.error }}
    </p>
  </div>
</template>
